import { AsyncPipe } from "@angular/common";
import {
    Component,
    inject,
    Injector,
    input,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { combineLatest, Observable } from "rxjs";
import {
    filter,
    map,
    share,
    shareReplay,
    startWith,
    tap
} from "rxjs/operators";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationMessageRowComponent } from "../../../../common/validation/validation-message-row/validation-message-row.component";
import { RaskSigneringStatus, VirkeskopskontraktDigitalSigneringViewModel } from "../../models/kontrakt-models";
import { UtskriftTyp } from "../../models/utskrift/utskrift-models";
import { IKontraktSigneringRequestDTO } from "../../models/utskrift/utskriftSignering-models";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { DigitalSigneringMediatorService } from "../../services/digitalsignering-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { KontraktSigneringComponent } from "../kontrakt-digitalsignering/kontrakt-signering/kontrakt-signering.component";
import { DigitalSigneringFormGroup, LeverantorFormGroup, UtskriftFormGroup } from "./digital-signernig-form";
import { KontraktDigitalsigneringHistorikComponent } from "./kontrakt-digitalsignering-historik/kontrakt-digitalsignering-historik.component";
import { KontraktDigitalsigneringItemComponent } from "./kontrakt-digitalsignering-item/kontrakt-digitalsignering-item.component";

@Component({
    selector: "rask-kontrakt-digitalsignering",
    templateUrl: "./kontrakt-digitalsignering.component.html",
    styleUrl: "./kontrakt-digitalsignering.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ValidationMessageRowComponent,
        KontraktDigitalsigneringItemComponent,
        ButtonComponent,
        BusySpinnerComponent,
        AsyncPipe
    ]
})
export class KontraktDigitalsigneringComponent implements OnInit {
    readonly dokumentTyp = input<UtskriftTyp>("Kontrakt");

    protected aktivSignering$: Observable<VirkeskopskontraktDigitalSigneringViewModel>;
    protected inaktivaSigneringar$: Observable<VirkeskopskontraktDigitalSigneringViewModel[]>;
    protected isKontraktIncluded = false;
    protected loading = true;
    protected sendContractForSigningDisabled$: Observable<boolean>;
    protected versionDifference$: Observable<boolean>;

    private readonly digitalSigneringMediator = inject(DigitalSigneringMediatorService);
    private readonly fb = inject(FormBuilder);
    private readonly form: DigitalSigneringFormGroup = this.fb.group({
        kontraktBenamning: [""],
        giltigAntalDagar: [
            1,
            [
                Validators.required,
                Validators.min(1)
            ]
        ],
        slutDatum: [""],
        leverantorer: this.fb.array<LeverantorFormGroup>([]),
        utskrifter: this.fb.array<UtskriftFormGroup>([])
    });
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly authService = inject(AuthenticationService);
    private readonly localInjector = inject(Injector);
    private readonly modalService = inject(NgbModal);
    private readonly raskModalService = inject(RaskModalService);

    ngOnInit(): void {
        const utskrift$ = this.digitalSigneringMediator.kontraktUtskrifter$.pipe(
            map(utskrifter => utskrifter.utskrifter.find(doc => doc.typ === this.dokumentTyp())),
            tap(utskrift => this.isKontraktIncluded = !!utskrift),
            share()
        );

        const signeringar$ = combineLatest([
            this.digitalSigneringMediator.kontraktsSigneringar$,
            utskrift$
        ]).pipe(
            tap(() => this.loading = false),
            map(([
                signeringar,
                utskrift
            ]) => signeringar.filter(s => s.dokumentNamn === utskrift?.dokumentNamn)),
            map(signeringar => signeringar.map(s => new VirkeskopskontraktDigitalSigneringViewModel(s)))
        );

        this.aktivSignering$ = signeringar$.pipe(
            map(signeringar => signeringar.find(s => s.aktiv)),
            shareReplay(1)
        );

        this.inaktivaSigneringar$ = signeringar$.pipe(
            map(signeringar => signeringar.filter(s => !s.aktiv)),
            shareReplay(1)
        );

        this.sendContractForSigningDisabled$ = this.aktivSignering$.pipe(
            map(signering => signering && ![
                RaskSigneringStatus.KontraktNedladdat,
                RaskSigneringStatus.SkickaPagar
            ].includes(signering.status))
        );

        this.versionDifference$ = combineLatest([
            this.aktivSignering$,
            utskrift$
        ]).pipe(
            filter(([
                current,
                _utskrift
            ]) => ObjectUtils.hasValue(current)),
            map(([
                current,
                utskrift
            ]) => current.skickadDokumentVersion !== utskrift.version),
            startWith(false)
        );
    }

    protected canSendContractForSigning(): boolean {
        return !this.kontraktMediatorService.isDirty() &&
            this.isKontraktIncluded &&
            this.kontraktMediatorService.kontrakt.isKontrakt && this.currentUserIsAnsvarigVirkeskopare() &&
            !this.loading;
    }

    protected doSignering(kontraktSignering: IKontraktSigneringRequestDTO) {
        this.loading = true;
        this.digitalSigneringMediator.sendContractForSigning({
            ...kontraktSignering,
            primaryDokumentTyp: this.dokumentTyp()
        });
    }

    protected openHistorik(signeringar: VirkeskopskontraktDigitalSigneringViewModel[]) {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "lg",
            injector: this.localInjector
        } as NgbModalOptions;
        this.raskModalService.open(
            "Tidigare signeringar",
            KontraktDigitalsigneringHistorikComponent,
            undefined,
            undefined,
            {
                signeringar: signeringar.filter(s => !s.aktiv)
            },
            undefined,
            options);
    }

    protected openSignering() {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "xl",
            injector: this.localInjector
        } as NgbModalOptions;
        const modalRef = this.modalService.open(KontraktSigneringComponent, options);
        const componentInstance = (modalRef.componentInstance as KontraktSigneringComponent);
        componentInstance.aktivSignering$ = this.aktivSignering$;
        componentInstance.dokumentTyp = this.dokumentTyp();

        this.form.patchValue({
            kontraktBenamning: this.kontraktMediatorService.kontrakt.benamning
        }, {
            emitEvent: false,
            onlySelf: true
        });

        componentInstance.form = this.form;

        modalRef.result.then(result => {
            if (result) {
                this.doSignering(result);
            }
        }, (error) => {
            //
        });
    }

    private currentUserIsAnsvarigVirkeskopare(): boolean {
        const currentUser = this.authService.getUserData();
        const ansvarigVirkeskopare = this.avtalMediatorService.avtal?.ansvarigVirkeskopare;
        return currentUser?.userName === ansvarigVirkeskopare?.userName;
    }
}
